import React, { useEffect, useMemo, useRef } from 'react';
import { useFormApi } from 'informed';

import { DayWiseSlotHoldDatesDetailInterface } from '@nw/shared/config';

import { useInformedSlotSelectionField } from '../../../hooks/useInformedSlotSelectionField';
import { SlotSelectionFieldTimeLapsedOptionType } from '../../../types';

import { SlotSelectionFieldFormattedOption } from './SlotSelectionFieldFormattedOption';

interface SlotSelectionFieldProps {
  field: string;
  supportingField: string;
  label?: string;
  slotOptions: SlotSelectionFieldTimeLapsedOptionType[];
  dayWiseSlotHoldDates: DayWiseSlotHoldDatesDetailInterface[];
  slotConditions: Record<string, string>;
  values: Record<string, any>;
  error: string;
  touched: boolean;
  isDependent?: boolean;
  dependentFieldValue?: string;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  validationSchemaObject: any;
  isDependentFieldTouched: boolean;
  intensiveSlotsLanguageOptions?: Record<string, string[]>;
  academySlotsLanguageOptions?: Record<string, string[]>;
  slotUIType?: string;
  dependentOnFields?: string[];
  containerClassName?: string;
  labelClassName?: string;
  isDateBasedSlots: boolean;
  showAllSlotsState?: boolean;
}

export const SlotSelectionField = (props: SlotSelectionFieldProps) => {
  const {
    values,
    field,
    dependentFieldValue,
    slotConditions,
    slotOptions,
    dayWiseSlotHoldDates,
    isDependentFieldTouched,
    supportingField,
    label,
    validationSchemaObject,
    error,
    touched,
    validateOnBlur,
    isDependent,
    intensiveSlotsLanguageOptions,
    academySlotsLanguageOptions,
    slotUIType,
    validateOnChange,
    dependentOnFields = [],
    containerClassName,
    labelClassName,
    isDateBasedSlots,
    showAllSlotsState = false
  } = props;

  const formApi = useFormApi();

  const executeChildFunction = useRef<(value: string) => void | null>(
    null
  ) as React.MutableRefObject<(value: string) => void | null>;

  const isDependentOnFieldsTouched = useMemo(() => {
    return dependentOnFields.some((field) => values[field]);
  }, [values, ...dependentOnFields.map((field) => values[field])]);

  useEffect(() => {
    if (isDependentFieldTouched || isDependentOnFieldsTouched) {
      formApi.setValue(field, '');
      if (executeChildFunction.current) {
        executeChildFunction.current('');
      }
    }
  }, [dependentFieldValue, ...dependentOnFields.map((field) => values[field])]);

  const formattedOptions: any = useInformedSlotSelectionField({
    values,
    dependentFieldValue,
    intensiveSlotsLanguageOptions,
    academySlotsLanguageOptions,
    slotConditions,
    slotOptions,
    dayWiseSlotHoldDates,
    isDependent,
    isDateBasedSlots
  });

  return (
    <SlotSelectionFieldFormattedOption
      showAllSlotsState={showAllSlotsState}
      field={field}
      supportingField={supportingField}
      label={label}
      options={formattedOptions}
      validationSchemaObject={validationSchemaObject}
      error={error}
      touched={touched}
      validateOnBlur={validateOnBlur}
      validateOnChange={validateOnChange}
      slotUIType={slotUIType}
      containerClassName={containerClassName}
      labelClassName={labelClassName}
      executeChildFunction={executeChildFunction}
    />
  );
};
